import React, { useCallback } from 'react';
import { useHistory, useParams } from 'react-router-dom';
import { StringParam, useQueryParams } from 'use-query-params';
import styled from 'styled-components';

import useErnie from 'shared/hooks/use-ernie';

import { ADMIN_CONTENT_BACKGROUND, CmsEditorFrame } from 'src/components/cms-editor-frame';

import { toWebStudioTabKey } from '../tab-keys';
import { useDefaultEnterpriseCName } from './use-default-enterprise-cname';

export default function WebStudioEditor(): JSX.Element {
  const { enterpriseId, modelId } = useParams<{ enterpriseId: string; modelId: string }>();
  const history = useHistory();
  // `tab` is set by the linking list page so navigating back returns to that same tab
  // instead of always resetting to Web Studio's first tab.
  const [{ variantKey, tab }, setQueryParams] = useQueryParams({ variantKey: StringParam, tab: StringParam });
  // The CMS editor always needs a store cName to preview against; default it from the
  // enterprise. `loading` is forwarded as `cNamePending` rather than gating this component's
  // own mount -- see the JSX comment below.
  const { cName, loading } = useDefaultEnterpriseCName(enterpriseId);
  const showErnie = useErnie();

  // Without this, a cms:error (e.g. auth failure on save) is silently dropped and the user
  // sees a failed save with no explanation.
  const handleEditorError = useCallback(
    (payload?: Record<string, unknown>) => {
      const message = typeof payload?.message === 'string' ? payload.message : 'Something went wrong in the editor.';
      showErnie(message, 'danger');
    },
    [showErnie]
  );

  // The embedded editor's own route is the single source of truth for modelId/variantKey --
  // `CmsEditorFrame` reports every change to it (switching pages via the header nav widget, a
  // first-publish rename, creating a new page) through this one callback, via `cms:location`
  // (see `useCmsEditorNavigate` on the marketplace side, and `CmsEditorFrame`'s own doc comment
  // for the echo guard that keeps this from bouncing back into a reload). This is the ONLY place
  // this route's own URL needs to change in response to the editor -- no per-feature wiring, and
  // no manual "freeze the value fed to the iframe" ref: CmsEditorFrame already recognizes its own
  // reported location coming back down as props and skips rebuilding/reloading the iframe.
  const handleLocationChange = useCallback(
    (location: { modelId: string; variantKey: string }) => {
      if (location.modelId !== modelId) {
        // A different model entirely -- modelId is a path segment (:modelId), not a query
        // param, so this needs a real route push, not setQueryParams. Preserve `tab` so this
        // still returns to the right Web Studio tab if the user exits afterward.
        const search = new URLSearchParams({ variantKey: location.variantKey });
        if (tab) search.set('tab', tab);
        history.push(`/enterprise/${enterpriseId}/web-studio/editor/${location.modelId}?${search.toString()}`);
        return;
      }
      if (location.variantKey !== variantKey) {
        setQueryParams({ variantKey: location.variantKey }, 'replaceIn');
      }
    },
    [modelId, variantKey, tab, enterpriseId, history, setQueryParams]
  );

  // The embedded editor's logo mark posts cms:navigate when clicked, asking to go back to the
  // Web Studio landing page. The payload carries no destination (only one place to go), so we
  // rely on this page's own `tab` query param to land back on the right tab.
  const returnTabKey = toWebStudioTabKey(tab);
  const handleEditorNavigate = useCallback(() => {
    const search = returnTabKey ? `?tab=${returnTabKey}` : '';
    history.push(`/enterprise/${enterpriseId}/web-studio${search}`);
  }, [history, enterpriseId, returnTabKey]);

  return (
    <EditorPage>
      {/*
       * CmsEditorFrame must mount immediately, before `cName` resolves -- do NOT reintroduce a
       * `{!loading && ...}` gate here. `useDefaultEnterpriseCName` runs two sequential Apollo
       * queries (two round-trips on a cold cache), during which this component would otherwise
       * render an empty, chrome-less page. CmsEditorFrame *is* this route's loading state: it
       * draws the rail on the first frame and starts the collapse via `cNamePending`, so the
       * sidebar's green bar appears to continuously resize into the editor's rail instead of
       * vanishing and reappearing once the queries settle.
       */}
      <CmsEditorFrame
        enterpriseId={enterpriseId}
        modelId={modelId}
        variantKey={variantKey ?? undefined}
        cName={cName}
        cNamePending={loading}
        onError={handleEditorError}
        onLocationChange={handleLocationChange}
        onNavigate={handleEditorNavigate}
      />
    </EditorPage>
  );
}

const EditorPage = styled.div`
  height: 100vh;
  width: 100vw;
  overflow: hidden;
  /* Same tone as CmsEditorFrame's loading overlay (ADMIN_CONTENT_BACKGROUND) -- avoids a
     bare white frame for the single paint before its overlay mounts. */
  background-color: ${ADMIN_CONTENT_BACKGROUND};
`;
